import { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";

// Routes configuration
import { getAllRoutes } from "./routes";

// Components
import CookieConsent from "./components/CookieConsent";
import Navigation from "./components/shared/Navigation";
import Footer from "./components/shared/Footer";
import { TrustBadgesSection } from "./components/shared/TrustBadges";
import { ReviewsSection, ALL_REVIEWS } from "./components/shared/Reviews";
import { usePageMeta, SchemaScript, generateHomeSchema, generatePersonSchema, generateWebSiteSchema, generateOrganizationSchema } from "./components/shared/SEOHelper";
import RedirectionGuard from "./components/shared/RedirectionGuard";
import NotFoundPage from "./pages/NotFoundPage";
import { Toaster } from "sonner";

// Logo component
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yoga-frome/artifacts/fbti6fnl_Annelie%20Lily%20Logo%20colored%20FA%2002.png";

const LotusLogo = ({ className = "w-12 h-12" }) => (
  <img 
    src={LOGO_URL} 
    alt="Annelie Lily lotus logo with heart" 
    className={className}
    width={48}
    height={48}
    style={{ objectFit: 'contain' }}
    loading="eager"
    decoding="async"
  />
);

// Mobile Sticky CTA
const MobileStickyButton = () => (
  <Link
    to="/contact"
    className="fixed bottom-6 right-6 z-40 lg:hidden bg-gold text-white px-6 py-3 rounded-full shadow-lg hover:bg-gold-dark transition-all duration-300 flex items-center gap-2 font-medium text-sm"
    data-testid="mobile-sticky-cta"
  >
    Book
    <ArrowRight className="w-4 h-4" />
  </Link>
);

// Hero Section - Client's exact wording
const HERO_IMAGE = "/images/hero-annelie-yoga.jpg";

const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-cream"
  >
    {/* Hero image - contain to show full poses without cropping */}
    <div 
      className="absolute inset-0"
      style={{
        backgroundImage: `url('${HERO_IMAGE}')`,
        backgroundSize: "contain",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
      }}
    />
    {/* Very strong fade edges - start from solid cream and fade out */}
    <div 
      className="absolute inset-y-0 left-0 w-[25%] z-[1]"
      style={{
        background: 'linear-gradient(to right, rgb(250, 247, 242) 0%, rgb(250, 247, 242) 40%, transparent 100%)'
      }}
    />
    <div 
      className="absolute inset-y-0 right-0 w-[25%] z-[1]"
      style={{
        background: 'linear-gradient(to left, rgb(250, 247, 242) 0%, rgb(250, 247, 242) 40%, transparent 100%)'
      }}
    />
    <div 
      className="absolute inset-x-0 bottom-0 h-24 z-[1]"
      style={{
        background: 'linear-gradient(to top, rgb(250, 247, 242) 0%, transparent 100%)'
      }}
    />
    <div 
      className="absolute inset-x-0 top-0 h-20 z-[1]"
      style={{
        background: 'linear-gradient(to bottom, rgb(250, 247, 242) 0%, transparent 100%)'
      }}
    />
    {/* Soft overall overlay for text readability */}
    <div className="absolute inset-0 bg-cream/50" />
    
    <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <LotusLogo className="w-16 h-16 mx-auto mb-6" />
        <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-neutral-900 mb-4">
          Yoga & Holistic Healing in Frome
        </h1>
        <p className="text-lg md:text-xl text-neutral-700 mb-3">
          Yoga Teacher & Holistic Healer
        </p>
        <p className="text-gold text-base md:text-lg tracking-wide mb-8 font-medium">
          Calm Your Mind • Nurture Your Body • Transform Your Life
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Link to="/yoga-classes#book" className="btn-primary px-8 py-3.5 text-base" data-testid="hero-book-class-btn">
            Book a Class
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// Introduction Section - Client's exact wording
const IntroSection = () => (
  <section className="section-padding bg-cream relative overflow-hidden">
    {/* Subtle decorative elements */}
    <div className="absolute top-0 left-0 w-64 h-64 bg-gold/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
    <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />
    
    <div className="container-max relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-6xl mx-auto"
      >
        <div className="grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Greeting + main content - LEFT */}
          <div>
            <div className="mb-8 text-center md:text-left">
              <div className="inline-flex items-center gap-4 mb-4">
                <span className="h-px w-12 bg-gold/40" />
                <span className="text-gold text-sm tracking-widest uppercase font-medium">Welcome</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-neutral-800">
                Hi, I'm Annelie.
              </h2>
            </div>

            <div className="space-y-5 text-center md:text-left">
              <p className="text-xl md:text-2xl font-heading text-neutral-700 leading-relaxed">
                I am a healer, yoga teacher and space-holder.
              </p>

              <div className="w-16 h-px bg-gold/30 mx-auto md:mx-0" />

              <p className="text-base md:text-lg text-neutral-600 leading-relaxed">
                My work is about supporting people to reconnect with themselves — physically, emotionally and energetically.
              </p>

              <p className="text-base md:text-lg text-neutral-600 leading-relaxed">
                I work in Frome and Exeter offering warm and welcoming yoga classes, holistic treatments and healing sessions, women's circles and retreats, creating supportive spaces for relaxation, healing and personal transformation.
              </p>
            </div>
          </div>

          {/* Annelie meditating in the loft room - RIGHT */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-gold/10">
              <img
                src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/6fn03bja_CC8E4CF2-ED69-4714-A6ED-C6A25F7F65A7.PNG"
                alt="Annelie Lily seated cross-legged in meditation in the warm loft room of her Grade II listed cottage in Frome, surrounded by candles and large windows with garden views"
                className="w-full h-full object-cover"
                width={800}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </div>
            {/* Soft accent dot */}
            <div className="hidden md:block absolute -top-3 -left-3 w-6 h-6 bg-gold/30 rounded-full" />
            <div className="hidden md:block absolute -bottom-4 -right-4 w-10 h-10 bg-gold/20 rounded-full" />
          </div>
        </div>

        {/* Cottage / space card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-14 max-w-4xl mx-auto"
        >
          <div className="relative bg-white p-8 md:p-10 rounded-2xl border border-gold/20 shadow-lg">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-10 h-10 bg-cream rounded-full flex items-center justify-center border border-gold/20">
                <div className="w-3 h-3 bg-gold rounded-full" />
              </div>
            </div>
            <p className="text-lg md:text-xl text-neutral-700 text-center leading-relaxed">
              I work from a beautiful, Grade II listed cottage on the outskirts of Frome. Sessions take place in a warm, nurturing loft room or a peaceful garden room — both designed to help you feel relaxed and completely at ease.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Deeper Message Section
const DeeperMessageSection = () => (
  <section className="section-padding bg-muted">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto"
      >
        <p className="text-gold text-sm tracking-wide mb-6 text-center font-medium">
          Yoga, Healing, Retreats and Embodied Practices for Deep Transformation
        </p>
        <div className="prose-content space-y-4 text-center">
          <p>
            A space to slow down, reconnect and come back to yourself.
          </p>
          <p>
            Through healing sessions, retreats, circles and yoga, I support you in moving through what feels stuck, reconnecting with your body, and creating meaningful change in your life.
          </p>
          <p>
            Whether you come to a retreat, a circle, a healing session or a class, this is a space where you can soften, be met where you are, and begin to shift what no longer feels aligned.
          </p>
        </div>
      </motion.div>

      {/* The two spaces - loft yoga room + treatment room */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="max-w-5xl mx-auto mt-12 grid md:grid-cols-2 gap-6"
      >
        <div className="space-y-3">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-md border border-gold/10">
            <img
              src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/85ch7iny_IMG_2885.jpg"
              alt="The warm loft yoga room inside Annelie's Grade II listed cottage in Frome with stone wall, Buddha artwork, chakra hanging, sunset painting, exposed wooden beams and two yoga mats with bolsters under soft lamp light"
              className="w-full h-full object-cover"
              width={800}
              height={600}
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="text-center text-sm text-neutral-600 italic">The loft yoga room</p>
        </div>
        <div className="space-y-3">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-md border border-gold/10">
            <img
              src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/i2hmz3ig_IMG_1863.jpeg"
              alt="The tranquil garden treatment room with massage table dressed in chakra blanket and natural light"
              className="w-full h-full object-cover"
              width={800}
              height={600}
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="text-center text-sm text-neutral-600 italic">The garden treatment room</p>
        </div>
      </motion.div>
    </div>
  </section>
);

// Ways to Work With Me Section - Client's exact wording
const WaysToWorkSection = () => {
  const offerings = [
    {
      title: "Healing & Treatments",
      description: "1:1 sessions to support emotional, energetic and physical healing. A space to be held, to release and to gently transform including massage and energy healing techniques.",
      link: "/treatments",
      cta: "Book a Treatment"
    },
    {
      title: "Retreats",
      description: "Time away from everyday life to reset, reconnect and go deeper. Combining yoga, healing, nature and rest.",
      link: "/retreats",
      cta: "Explore Retreats"
    },
    {
      title: "Women's Circles",
      description: "A space for women to gather, share and reconnect in a supportive and grounded environment.",
      link: "/womens-circles",
      cta: "Find a Circle"
    },
    {
      title: "Yoga Classes",
      description: "Weekly classes to support movement, breath and awareness — a pathway into deeper connection and healing.",
      link: "/yoga-classes",
      cta: "View Classes"
    }
  ];

  return (
    <section className="section-padding bg-cream">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="label-text">What I Offer</span>
          <h2 className="section-heading">Ways to Work with Me</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {offerings.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link
                to={item.link}
                className="block bg-white/80 p-6 rounded-xl border border-gold/10 hover:border-gold/30 hover:shadow-md transition-all h-full group"
                data-testid={`offering-card-${index}`}
              >
                <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                  {item.title}
                </h3>
                <p className="text-neutral-600 text-sm leading-relaxed mb-4">{item.description}</p>
                <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  {item.cta} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// This Work Section - Client's exact wording
const ThisWorkSection = () => (
  <section className="section-padding bg-muted">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto"
      >
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
          This Work is Not About Fixing Yourself
        </h2>
        <div className="prose-content space-y-4 text-center">
          <p>
            It is about slowing down enough to listen, to feel what is there, and to create space for change to happen naturally.
          </p>
          <p>
            Many people come to me feeling tired, disconnected or stuck in patterns they don't fully understand.
          </p>
          <p>
            Through this work, something begins to shift.
          </p>
          <p className="text-lg font-medium text-neutral-800">
            There is more clarity, more ease, and a deeper connection to yourself and your life.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// Why Work With Me Section - Client's exact wording
const WhyWorkWithMeSection = () => (
  <section className="section-padding bg-cream">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto"
      >
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
          Why Work with Me
        </h2>
        <div className="prose-content space-y-4 text-center">
          <p>
            I have been teaching yoga since 2012 and holding space through retreats, circles and healing work since 2015.
          </p>
          <p>
            My approach is grounded, intuitive and supportive.
          </p>
          <p>
            This is not about pushing or forcing change — it is about creating a safe space where transformation can unfold in its own time.
          </p>
        </div>
        <div className="text-center mt-8">
          <Link to="/about" className="btn-secondary inline-flex items-center gap-2">
            Learn More About Me <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// Testimonials - Use shared Reviews component
const Testimonials = () => (
  <ReviewsSection 
    reviews={ALL_REVIEWS.slice(0, 3)}
    title="Community Voices"
    subtitle="Reviews from our Facebook community"
    showFacebookButton={true}
    columns={3}
  />
);

// How to Book Section - Client's exact wording
const HowToBookSection = () => (
  <section className="section-padding bg-cream">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto text-center"
      >
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">
          How to Book
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link 
            to="/treatments"
            className="bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
          >
            <p className="font-medium text-neutral-800 group-hover:text-gold transition-colors">Book a Treatment or Healing</p>
          </Link>
          <Link 
            to="/yoga-classes"
            className="bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
          >
            <p className="font-medium text-neutral-800 group-hover:text-gold transition-colors">Book a Yoga Class</p>
          </Link>
          <Link 
            to="/womens-circles"
            className="bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
          >
            <p className="font-medium text-neutral-800 group-hover:text-gold transition-colors">Book a Women's Circle</p>
          </Link>
          <Link 
            to="/retreats"
            className="bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
          >
            <p className="font-medium text-neutral-800 group-hover:text-gold transition-colors">Book a Retreat</p>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="section-padding bg-neutral-800 text-white">
    <div className="container-max">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-2xl mx-auto"
      >
        <p className="text-gold text-sm tracking-wide mb-4">Calm Your Mind • Nurture Your Body • Transform Your Life</p>
        <h2 className="font-heading text-3xl md:text-4xl mb-4">Begin Your Journey</h2>
        <p className="text-neutral-300 mb-8">
          To begin your journey with Annelie, you are invited to join a class, book a treatment or healing, attend a circle or explore an upcoming retreat.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="bg-gold hover:bg-gold-dark text-white px-8 py-3 rounded-full font-medium transition-colors">
            Get in Touch
          </Link>
          <a href="tel:07540995021" className="border border-white/30 hover:border-white text-white px-8 py-3 rounded-full font-medium transition-colors">
            Call 07540 995021
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

// Home Page Component
const HomePage = () => {
  usePageMeta({
    title: "Yoga & Holistic Healing in Frome, Somerset | Annelie Lily",
    description: "5-star yoga classes, holistic treatments, women's circles and retreats in Frome, Somerset. Calm your mind, nurture your body, transform your life.",
    canonical: "https://annelielily.com/",
    ogImage: "https://annelielily.com/og/home.png",
  });

  return (
    <>
      <SchemaScript schema={generateWebSiteSchema()} />
      <SchemaScript schema={generateOrganizationSchema()} />
      <SchemaScript schema={generateHomeSchema()} />
      <SchemaScript schema={generatePersonSchema()} />
      <Navigation />
      <MobileStickyButton />
      <main>
        <Hero />
        <IntroSection />
        <TrustBadgesSection />
        <DeeperMessageSection />
        <WaysToWorkSection />
        <ThisWorkSection />
        <Testimonials />
        <WhyWorkWithMeSection />
        <HowToBookSection />
        <CTASection />
      </main>
      <Footer />
    </>
  );
};

// Main App with Routing
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          {/* All routes loaded from routes.js config */}
          {getAllRoutes().map(({ path, element: Element }) => (
            <Route key={path} path={path} element={<Element />} />
          ))}
          {/* Dedicated NotFound route for build-time 404.html prerender */}
          <Route path="/__prerender_404__" element={<NotFoundPage />} />
          {/* 404 catch-all - intelligent redirect to the most relevant page */}
          <Route path="*" element={<RedirectionGuard />} />
        </Routes>
        <CookieConsent />
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
